import { Box, Crosshair, Globe, LocateFixed } from "lucide-react";
import type { ReactNode } from "react";
import { useStore } from "../app/store.ts";
import type {
  PoseModeState,
  SnapCursorToKind,
  SnapSelectionToCursorKind,
  WorkspaceMode,
} from "../app/storeTypes.ts";
import {
  hasAnySelection,
  hasFaceSelection,
} from "../modeling/editSelection.ts";
import type { EditModeState } from "../modeling/types.ts";
import { SnapCursorToIcon, SnapToCursorIcon } from "./Icons.tsx";

export interface SnapMenuState {
  workspaceMode: WorkspaceMode;
  selectedObjectCount: number;
  editMode: EditModeState | null;
  poseMode: PoseModeState | null;
}

interface SnapMenuOptionBase<TValue extends string> {
  id: string;
  value: TValue;
  label: string;
  pieLabel: string;
  renderIcon: (size: number) => ReactNode;
  shortcut?: string;
  modes?: ReadonlyArray<WorkspaceMode>;
  isEnabled?: (state: SnapMenuState) => boolean;
}

export interface SnapCursorOption extends SnapMenuOptionBase<SnapCursorToKind> {
  group: "cursor";
}

export interface SnapSelectionOption
  extends SnapMenuOptionBase<SnapSelectionToCursorKind> {
  group: "selection";
}

export type SnapMenuOption = SnapCursorOption | SnapSelectionOption;

const hasObjectSelection = (state: SnapMenuState): boolean =>
  state.selectedObjectCount > 0;

const hasEditSelection = (state: SnapMenuState): boolean =>
  !!state.editMode && hasAnySelection(state.editMode);

const hasPoseActiveBone = (state: SnapMenuState): boolean =>
  state.poseMode?.activeBone != null;

const canSnapCursorToSelection = (state: SnapMenuState): boolean =>
  hasPoseActiveBone(state) ||
  hasEditSelection(state) ||
  hasObjectSelection(state);

const canSnapSelectionToCursor = (state: SnapMenuState): boolean =>
  hasEditSelection(state) || hasObjectSelection(state);

export const SNAP_CURSOR_TO_OPTIONS: ReadonlyArray<SnapCursorOption> = [
  {
    id: "cursor:selection",
    group: "cursor",
    value: "selection",
    label: "Selection",
    pieLabel: "Cursor to Selection",
    renderIcon: (size) => <SnapCursorToIcon size={size} />,
    modes: ["object", "edit", "pose"],
    isEnabled: canSnapCursorToSelection,
  },
  {
    id: "cursor:selectionActive",
    group: "cursor",
    value: "selectionActive",
    label: "Active Element",
    pieLabel: "Cursor to Active Element",
    renderIcon: (size) => <Crosshair size={size} />,
    modes: ["object", "edit"],
    isEnabled: (state) => hasEditSelection(state) || hasObjectSelection(state),
  },
  {
    id: "cursor:selectionFace",
    group: "cursor",
    value: "selectionFace",
    label: "Face Center",
    pieLabel: "Cursor to Face Center",
    renderIcon: (size) => <LocateFixed size={size} />,
    modes: ["edit"],
    isEnabled: (state) => !!state.editMode && hasFaceSelection(state.editMode),
  },
  {
    id: "cursor:activeBone",
    group: "cursor",
    value: "activeBone",
    label: "Active Bone",
    pieLabel: "Cursor to Active Bone",
    renderIcon: (size) => <Crosshair size={size} />,
    modes: ["pose"],
    isEnabled: hasPoseActiveBone,
  },
  {
    id: "cursor:grid",
    group: "cursor",
    value: "grid",
    label: "Grid",
    pieLabel: "Cursor to Grid",
    renderIcon: (size) => <Box size={size} />,
    modes: ["object", "edit", "pose"],
  },
  {
    id: "cursor:worldOrigin",
    group: "cursor",
    value: "worldOrigin",
    label: "World Origin",
    pieLabel: "Cursor to World Origin",
    renderIcon: (size) => <Globe size={size} />,
    shortcut: "Shift+C",
    modes: ["object", "edit", "pose"],
  },
];

export const SNAP_SELECTION_TO_CURSOR_OPTIONS: ReadonlyArray<SnapSelectionOption> =
  [
    {
      id: "selection:selection",
      group: "selection",
      value: "selection",
      label: "Selection",
      pieLabel: "Selection to Cursor",
      renderIcon: (size) => <SnapToCursorIcon size={size} />,
      modes: ["object", "edit"],
      isEnabled: canSnapSelectionToCursor,
    },
    {
      id: "selection:offset",
      group: "selection",
      value: "offset",
      label: "Selection (Offset)",
      pieLabel: "Selection to Cursor (Offset)",
      renderIcon: (size) => <LocateFixed size={size} />,
      modes: ["object", "edit"],
      isEnabled: canSnapSelectionToCursor,
    },
    {
      id: "selection:origin",
      group: "selection",
      value: "origin",
      label: "Origins",
      pieLabel: "Origins to Cursor",
      renderIcon: (size) => <Crosshair size={size} />,
      modes: ["object"],
      isEnabled: hasObjectSelection,
    },
  ];

export function isSnapMenuOptionVisible(
  option: SnapMenuOption,
  workspaceMode: WorkspaceMode,
): boolean {
  return !option.modes || option.modes.includes(workspaceMode);
}

export function isSnapMenuOptionEnabled(
  option: SnapMenuOption,
  state: SnapMenuState,
): boolean {
  return option.isEnabled?.(state) ?? true;
}

export function getVisibleSnapCursorOptions(
  workspaceMode: WorkspaceMode,
): ReadonlyArray<SnapCursorOption> {
  return SNAP_CURSOR_TO_OPTIONS.filter((option) =>
    isSnapMenuOptionVisible(option, workspaceMode),
  );
}

export function getVisibleSnapSelectionOptions(
  workspaceMode: WorkspaceMode,
): ReadonlyArray<SnapSelectionOption> {
  return SNAP_SELECTION_TO_CURSOR_OPTIONS.filter((option) =>
    isSnapMenuOptionVisible(option, workspaceMode),
  );
}

export function getSnapPieOptions(
  state: SnapMenuState,
): ReadonlyArray<SnapMenuOption> {
  return [
    ...getVisibleSnapCursorOptions(state.workspaceMode),
    ...getVisibleSnapSelectionOptions(state.workspaceMode),
  ];
}

export function runSnapMenuOption(option: SnapMenuOption): void {
  const state = useStore.getState();
  if (option.group === "cursor") {
    state.snapCursorTo(option.value);
    return;
  }
  state.snapSelectionToCursor(option.value);
}
